import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa";
import Select from "react-select";

import RefreshActionButton from "components/atoms/refresh-action-button";
import { EntityIdentityCell, StatusPill } from "components/admin";
import { SchoolMatchWeekHighlights } from "components/admin/school/school-match-week-highlights";
import { CrudSection } from "components/admin/workspace-crud";
import { getSelectFieldStyles, SelectFieldOption } from "components/global/select-field-styles";
import { DataTable } from "components/molecules/table/table";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import {
  buildPlayerPerformanceCreateValues,
  buildPlayerPerformanceFields,
  buildPlayerPerformanceUpdateValues,
} from "pages/app/workspace/sections/forms/school-ops-section-forms";
import {
  PlayerPerformanceRecordsTableSkeleton,
  PlayerPerformanceRosterSkeleton,
  PlayerPerformanceSectionSkeleton,
} from "pages/app/workspace/sections/player-performance-section.skeleton";
import {
  PlayerPerformanceEmptyPlayers,
  PlayerPerformanceFilterActions,
  PlayerPerformanceFilterBar,
  PlayerPerformanceAthleteFilterRow,
  PlayerPerformanceDesktopFilterRow,
  PlayerPerformanceFilterInputRow,
  PlayerPerformanceFilterLabel,
  PlayerPerformanceFilterSelectField,
  PlayerPerformanceMobileFilterBlock,
  PlayerPerformanceMobileSelectedPlayerCard,
  PlayerPerformanceMobileSelectedPlayerMeta,
  PlayerPerformanceMasterDetailGrid,
  PlayerPerformancePageStack,
  PlayerPerformancePlayerButton,
  PlayerPerformancePlayerList,
  PlayerPerformancePlayerPanel,
  PlayerPerformancePlayerPanelBody,
  PlayerPerformancePlayerPanelHeader,
  PlayerPerformancePlayerPanelTitle,
  PlayerPerformanceRecordsPanel,
  PlayerPerformanceSearchInput,
  PlayerPerformanceSelectedPlayerMeta,
  PlayerPerformanceSelectedPlayerWrap,
  PlayerPerformanceWorkspaceSection,
} from "pages/app/workspace/sections/styles/player-performance-section.inline.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDate } from "utils/admin";
import { hasAnyPortalAction } from "utils/portal-access";
import {
  PrimaryHeaderButton,
  SecondaryHeaderButton,
} from "components/admin/styles/workspace-crud.styles";

const getPlayerTeamsLabel = (player: any) =>
  (player.school_teams || []).map((team: any) => team.name).join(", ") || "No teams linked";

const getPlayerContextMeta = (player: any, recordCount: number) => {
  const details = [
    player.position || null,
    player.school_grade ? `Grade ${player.school_grade}` : null,
    getPlayerTeamsLabel(player),
    `${recordCount} record${recordCount === 1 ? "" : "s"}`,
  ].filter(Boolean);

  return details.join(" · ");
};

export const PlayerPerformanceSection: React.FC<{ currentUser: any; portalAccess: any }> = ({
  currentUser,
  portalAccess,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isAthleteAccount = Boolean(currentUser?.player?.id);
  const queryPlayerId = useMemo(
    () => new URLSearchParams(location.search).get("playerId") || "",
    [location.search],
  );
  const [playerId, setPlayerId] = useState(
    () => currentUser?.player?.id || queryPlayerId || "",
  );
  const [playerSearch, setPlayerSearch] = useState("");
  const [formDataRequested, setFormDataRequested] = useState(false);
  const hasResolvedInitialViewRef = useRef(false);
  const { schoolId } = useScopedSchoolSelection({
    portalAccess,
  });

  const schoolPlayersQuery = useQuery({
    queryKey: ["workspace-performance-players", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId) && !isAthleteAccount,
  });
  const schoolDashboardQuery = useQuery({
    queryKey: ["workspace-performance-school-dashboard", schoolId],
    queryFn: () => workspaceApi.schoolDashboards.get(schoolId),
    enabled: Boolean(schoolId) && !isAthleteAccount,
  });
  const sportsQuery = useQuery({
    queryKey: ["workspace-performance-sports"],
    queryFn: () => workspaceApi.sports.list(),
    enabled: formDataRequested,
  });
  const schoolTeamsQuery = useQuery({
    queryKey: ["workspace-performance-school-teams", schoolId],
    queryFn: () => workspaceApi.schoolTeams.list(schoolId),
    enabled: formDataRequested && Boolean(schoolId),
  });
  const schoolMatchesQuery = useQuery({
    queryKey: ["workspace-performance-school-matches", schoolId],
    queryFn: () => workspaceApi.schoolMatches.list({ school_id: schoolId || undefined }),
    enabled: formDataRequested && Boolean(schoolId),
  });

  const schoolPlayers = useMemo(
    () => schoolPlayersQuery.data || [],
    [schoolPlayersQuery.data],
  );
  const playerSelectOptions = useMemo<SelectFieldOption[]>(
    () =>
      schoolPlayers.map((player: any) => ({
        label: player.full_name || "Unknown",
        value: String(player.id),
      })),
    [schoolPlayers],
  );
  const selectedPlayerOption = useMemo(
    () => playerSelectOptions.find((option) => option.value === String(playerId)) || null,
    [playerId, playerSelectOptions],
  );
  const playerSelectStyles = useMemo(() => getSelectFieldStyles(), []);
  const filteredPlayers = useMemo(() => {
    const normalizedSearch = playerSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return schoolPlayers;
    }

    return schoolPlayers.filter((player: any) =>
      String(player.full_name || "")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [playerSearch, schoolPlayers]);

  useEffect(() => {
    if (isAthleteAccount) {
      return;
    }

    if (!queryPlayerId) {
      return;
    }

    if (queryPlayerId !== playerId) {
      setPlayerId(queryPlayerId);
    }
  }, [isAthleteAccount, playerId, queryPlayerId]);

  const selectPlayer = (nextPlayerId: string) => {
    setPlayerId(nextPlayerId);

    if (isAthleteAccount) {
      return;
    }

    const params = new URLSearchParams(location.search);

    if (nextPlayerId) {
      params.set("playerId", nextPlayerId);
    } else {
      params.delete("playerId");
    }

    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    });
  };

  const recordsQuery = useQuery({
    queryKey: ["workspace-performance-records", playerId],
    queryFn: () => workspaceApi.playerPerformanceRecords.list(playerId),
    enabled: Boolean(playerId),
    placeholderData: keepPreviousData,
  });
  const selectedPlayer = useMemo(
    () => schoolPlayers.find((player: any) => String(player.id) === String(playerId)),
    [playerId, schoolPlayers],
  );
  const canManagePerformanceRecords = hasAnyPortalAction(portalAccess, [
    "player_performance_records.manage",
    "player_performance_records.manage_own",
  ]);
  const performanceFields = buildPlayerPerformanceFields({
    schoolMatches: schoolMatchesQuery.data || [],
    schoolTeams: schoolTeamsQuery.data || [],
    sports: sportsQuery.data || [],
  });
  const performanceColumns = [
    { label: "Source", accessor: (row: any) => row.source_name || "Unknown" },
    { label: "Type", accessor: (row: any) => <StatusPill value={row.source_type} /> },
    { label: "Season", accessor: (row: any) => row.season || "Unknown" },
    { label: "Sport", accessor: (row: any) => row.sport_name || "Unknown" },
    { label: "Recorded", accessor: (row: any) => formatDate(row.recorded_on) },
  ];
  const performanceDescription = isAthleteAccount
    ? canManagePerformanceRecords
      ? "Review and add or update your logged performance records."
      : "Review your logged performance records."
    : canManagePerformanceRecords
      ? "View match-week highlights for the selected school, choose a player, and add or update their performance records."
      : "View match-week highlights and performance records for players at the selected school.";
  const recordsEmptyTitle = isAthleteAccount || playerId
    ? "No performance records yet"
    : "Select a player to view records";
  const recordsEmptyDescription = isAthleteAccount
    ? "Your performance records will appear here once they are logged."
    : playerId
      ? "Performance records will appear here once they are logged for the selected player."
      : "Choose a player from the roster to review or add performance records.";
  const isStaffSchoolContextPending = !isAthleteAccount && !schoolId;
  const isStaffDataLoading =
    !isAthleteAccount &&
    Boolean(schoolId) &&
    (schoolDashboardQuery.isPending || schoolPlayersQuery.isPending);
  const isStaffRecordsBootstrapping =
    !hasResolvedInitialViewRef.current &&
    !isAthleteAccount &&
    Boolean(schoolId) &&
    Boolean(playerId) &&
    recordsQuery.isPending;
  const isAthletePageLoading =
    !hasResolvedInitialViewRef.current &&
    isAthleteAccount &&
    Boolean(playerId) &&
    recordsQuery.isPending;
  const isStaffPageLoading =
    isStaffSchoolContextPending || isStaffDataLoading || isStaffRecordsBootstrapping;
  const isPageLoading = isAthletePageLoading || isStaffPageLoading;
  const showRecordsTableSkeleton =
    Boolean(playerId) && recordsQuery.isPending && hasResolvedInitialViewRef.current;
  const recordsTableLoadingState = (
    <PlayerPerformanceRecordsTableSkeleton />
  );

  useEffect(() => {
    if (isAthleteAccount) {
      if (!playerId || !recordsQuery.isPending) {
        hasResolvedInitialViewRef.current = true;
      }
      return;
    }

    if (!schoolId) {
      return;
    }

    if (
      !schoolDashboardQuery.isPending &&
      !schoolPlayersQuery.isPending &&
      (!playerId || !recordsQuery.isPending)
    ) {
      hasResolvedInitialViewRef.current = true;
    }
  }, [
    isAthleteAccount,
    playerId,
    recordsQuery.isPending,
    schoolDashboardQuery.isPending,
    schoolId,
    schoolPlayersQuery.isPending,
  ]);

  const prefetchFormReferenceData = () => {
    setFormDataRequested(true);
  };

  const renderSelectedPlayerSummary = (recordCount: number, compact = false) => {
    if (!selectedPlayer) {
      return (
        <PlayerPerformanceSelectedPlayerMeta>
          Select a player to review or add performance records.
        </PlayerPerformanceSelectedPlayerMeta>
      );
    }

    return (
      <>
        <EntityIdentityCell
          fallbackLabel={selectedPlayer.full_name}
          imageAlt={selectedPlayer.full_name || "Player"}
          imageSrc={
            selectedPlayer.avatar_url || selectedPlayer.image_url || selectedPlayer.image
          }
          primaryText={selectedPlayer.full_name || "Unknown"}
          secondaryText={selectedPlayer.position || "Position pending"}
          size={compact ? "sm" : "md"}
          variant="athlete"
        />
        {compact ? (
          <PlayerPerformanceMobileSelectedPlayerMeta>
            {getPlayerContextMeta(selectedPlayer, recordCount)}
          </PlayerPerformanceMobileSelectedPlayerMeta>
        ) : (
          <PlayerPerformanceSelectedPlayerMeta>
            {getPlayerContextMeta(selectedPlayer, recordCount)}
          </PlayerPerformanceSelectedPlayerMeta>
        )}
      </>
    );
  };

  const renderFilterActions = (listProps: {
    canCreate?: boolean;
    createLabel?: string;
    isRefreshing?: boolean;
    onCreate?: () => void;
    onRefresh?: () => void | Promise<unknown>;
  }) => (
    <PlayerPerformanceFilterActions>
      {listProps.onRefresh ? (
        <RefreshActionButton
          ButtonComponent={SecondaryHeaderButton}
          isLoading={listProps.isRefreshing}
          label="Refresh"
          loadingLabel="Refreshing..."
          onClick={() => listProps.onRefresh?.()}
        />
      ) : null}
      {listProps.canCreate && listProps.onCreate ? (
        <PrimaryHeaderButton
          onClick={() => {
            prefetchFormReferenceData();
            listProps.onCreate?.();
          }}
          type="button"
        >
          <FaPlus size={12} />
          {listProps.createLabel || "Add performance record"}
        </PrimaryHeaderButton>
      ) : null}
    </PlayerPerformanceFilterActions>
  );

  const renderFilterBar = (listProps: {
    canCreate?: boolean;
    createLabel?: string;
    data: any[];
    isRefreshing?: boolean;
    onCreate?: () => void;
    onRefresh?: () => void | Promise<unknown>;
  }) => (
    <PlayerPerformanceFilterBar>
      {!isAthleteAccount ? (
        <>
          <PlayerPerformanceMobileFilterBlock>
            <PlayerPerformanceFilterLabel htmlFor="player-performance-mobile-filter">
              Select player
            </PlayerPerformanceFilterLabel>
            <PlayerPerformanceFilterInputRow>
              <PlayerPerformanceFilterSelectField>
                <Select
                  classNamePrefix="player-performance-select"
                  inputId="player-performance-mobile-filter"
                  isDisabled={schoolPlayersQuery.isLoading || !schoolId}
                  isSearchable
                  menuPlacement="auto"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  onChange={(option) => selectPlayer(option?.value ?? "")}
                  options={playerSelectOptions}
                  placeholder="Select a player"
                  styles={playerSelectStyles}
                  value={selectedPlayerOption}
                />
              </PlayerPerformanceFilterSelectField>
              {renderFilterActions(listProps)}
            </PlayerPerformanceFilterInputRow>

            {selectedPlayer ? (
              <PlayerPerformanceMobileSelectedPlayerCard>
                {renderSelectedPlayerSummary(listProps.data.length, true)}
              </PlayerPerformanceMobileSelectedPlayerCard>
            ) : null}
          </PlayerPerformanceMobileFilterBlock>

          <PlayerPerformanceDesktopFilterRow>
            <PlayerPerformanceSelectedPlayerWrap>
              {renderSelectedPlayerSummary(listProps.data.length)}
            </PlayerPerformanceSelectedPlayerWrap>
            {renderFilterActions(listProps)}
          </PlayerPerformanceDesktopFilterRow>
        </>
      ) : (
        <PlayerPerformanceAthleteFilterRow>
          <PlayerPerformanceFilterLabel>Your performance records</PlayerPerformanceFilterLabel>
          {renderFilterActions(listProps)}
        </PlayerPerformanceAthleteFilterRow>
      )}
    </PlayerPerformanceFilterBar>
  );

  return (
    <PlayerPerformancePageStack>
      {isPageLoading ? (
        <PlayerPerformanceSectionSkeleton
          isAthleteAccount={isAthleteAccount}
          showHighlights={!isAthleteAccount && Boolean(schoolId)}
        />
      ) : (
      <CrudSection
        buildCreateValues={buildPlayerPerformanceCreateValues}
        buildUpdateValues={buildPlayerPerformanceUpdateValues}
        canCreate={canManagePerformanceRecords && Boolean(playerId)}
        canEdit={canManagePerformanceRecords && Boolean(playerId)}
        canRemove={canManagePerformanceRecords && Boolean(playerId)}
        columns={performanceColumns}
        createLabel="Add performance record"
        data={recordsQuery.data || []}
        description={performanceDescription}
        emptyDescription={recordsEmptyDescription}
        emptyTitle={recordsEmptyTitle}
        fields={performanceFields}
        hideHeaderActions
        invalidateKeys={[["workspace-performance-records", playerId]]}
        isLoading={showRecordsTableSkeleton}
        onCreate={(payload) => workspaceApi.playerPerformanceRecords.create(playerId, payload)}
        onRemove={(row) => workspaceApi.playerPerformanceRecords.remove(playerId, row.id)}
        onUpdate={(row, payload) =>
          workspaceApi.playerPerformanceRecords.update(playerId, row.id, payload)
        }
        renderList={(listProps) => (
          <>
            {!isAthleteAccount && schoolId ? (
              <SchoolMatchWeekHighlights
                matchWeek={schoolDashboardQuery.data?.match_week}
                onSelectPlayer={selectPlayer}
                subtitle="Match-week snapshot for the selected school. Tap player of the week to open their records."
                weeklyHighlights={schoolDashboardQuery.data?.weekly_highlights}
              />
            ) : null}

            <PlayerPerformanceWorkspaceSection>
              {isAthleteAccount ? (
              <PlayerPerformanceRecordsPanel>
                {renderFilterBar(listProps)}
                <DataTable
                  canEdit={listProps.canEdit}
                  canRemove={listProps.canRemove}
                  columns={listProps.columns}
                  data={listProps.data}
                  emptyDescription={listProps.emptyDescription}
                  emptyTitle={listProps.emptyTitle}
                  hideNav
                  isLoading={showRecordsTableSkeleton}
                  loadingState={recordsTableLoadingState}
                  onEdit={listProps.onEdit}
                  onRemove={
                    listProps.onRemove
                      ? (_, row) => row && listProps.onRemove?.(row)
                      : undefined
                  }
                  rowKey={listProps.rowKey}
                />
              </PlayerPerformanceRecordsPanel>
            ) : (
              <PlayerPerformanceMasterDetailGrid>
                <PlayerPerformancePlayerPanel>
                  <PlayerPerformancePlayerPanelHeader>
                    <PlayerPerformancePlayerPanelTitle>Players</PlayerPerformancePlayerPanelTitle>
                    <PlayerPerformanceSearchInput
                      onChange={(event) => setPlayerSearch(event.target.value)}
                      value={playerSearch}
                    />
                  </PlayerPerformancePlayerPanelHeader>
                  <PlayerPerformancePlayerPanelBody>
                    <PlayerPerformancePlayerList>
                      {!schoolId ? (
                        <PlayerPerformanceEmptyPlayers>
                          Select a school to load the player roster.
                        </PlayerPerformanceEmptyPlayers>
                      ) : schoolPlayersQuery.isLoading ? (
                        <PlayerPerformanceRosterSkeleton />
                      ) : filteredPlayers.length === 0 ? (
                        <PlayerPerformanceEmptyPlayers>
                          No players match this search for the selected school.
                        </PlayerPerformanceEmptyPlayers>
                      ) : (
                        filteredPlayers.map((player: any) => (
                          <PlayerPerformancePlayerButton
                            $active={String(player.id) === String(playerId)}
                            key={player.id}
                            onClick={() => selectPlayer(String(player.id))}
                            type="button"
                          >
                            <EntityIdentityCell
                              fallbackLabel={player.full_name}
                              imageAlt={player.full_name || "Player"}
                              imageSrc={player.avatar_url || player.image_url || player.image}
                              primaryText={player.full_name || "Unknown"}
                              secondaryText={getPlayerTeamsLabel(player)}
                              size="sm"
                              variant="athlete"
                            />
                          </PlayerPerformancePlayerButton>
                        ))
                      )}
                    </PlayerPerformancePlayerList>
                  </PlayerPerformancePlayerPanelBody>
                </PlayerPerformancePlayerPanel>

                <PlayerPerformanceRecordsPanel>
                  {renderFilterBar(listProps)}
                  <DataTable
                    canEdit={listProps.canEdit}
                    canRemove={listProps.canRemove}
                    columns={listProps.columns}
                    data={listProps.data}
                    emptyDescription={listProps.emptyDescription}
                    emptyTitle={listProps.emptyTitle}
                    hideNav
                    isLoading={showRecordsTableSkeleton}
                    loadingState={recordsTableLoadingState}
                    onEdit={(row) => {
                      prefetchFormReferenceData();
                      listProps.onEdit?.(row);
                    }}
                    onRemove={
                      listProps.onRemove
                        ? (_, row) => row && listProps.onRemove?.(row)
                        : undefined
                    }
                    rowKey={listProps.rowKey}
                  />
                </PlayerPerformanceRecordsPanel>
              </PlayerPerformanceMasterDetailGrid>
              )}
            </PlayerPerformanceWorkspaceSection>
          </>
        )}
        title="Performance & Highlights"
      />
      )}
    </PlayerPerformancePageStack>
  );
};
